import { Component, Input, OnInit, inject } from "@angular/core";
import { AuditFieldworkPermissionModel } from "@app/audit/shared/permission-models/audit-fieldwork-permission-model";
import { RiskLinkableModel } from "@app/risk/models/risk-linkable-model";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { nameof } from "@methods/jeffs-toolkit";
import { map, firstValueFrom } from "rxjs";
import { TabService } from "../../../../../../services/tabs/TabService";
import { AuditFieldworkStepDataService } from "../../../../services/audit-fieldworkstep-data.service";

@Component({
    selector: "audit-fieldwork-step-risk-linking",
    templateUrl: "./audit-fieldwork-step-risk-linking.component.html",
    styleUrls: ["./audit-fieldwork-step-risk-linking.component.scss"],
    standalone: false
})
export class AuditFieldworkStepRiskLinkingComponent implements OnInit {
    private _ds = inject(AuditFieldworkStepDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);

    @Input() auditGuid: string;
    @Input() stepGuid: string;
    @Input() permissions: AuditFieldworkPermissionModel;
    @Input() linkedRisks: number[];

    tableConfig: GenericListConfig;

    async ngOnInit() {
        if (!this.permissions || !this.permissions.canViewLinkedRisks) {
            return;
        }

        this.createTableConfig();
    }

    openRisk(row: RiskLinkableModel) {
        if (row) {
            this._tabService.generateTab(this._pages.RiskDetail, row.guid);
        }
    }

    private createTableConfig() {
        this.tableConfig = <GenericListConfig>{
            name: "Linked Risks",
            idProp: "id",

            allowAdd: false,
            allowEdit: this.permissions.canViewLinkedRisks,
            allowLink: this.permissions.canLinkRisks,
            linkData: <any>{
                displayValueProp: "name",
                linkablePaginationItemCount: 15,
                hidePanelToggle: true,
            },

            fields: [
                {
                    prettyName: "Name",
                    fieldName: nameof<RiskLinkableModel>((x) => x.name),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: "Owner",
                    fieldName: nameof<RiskLinkableModel>((x) => x.owner),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    prettyName: "Organization",
                    fieldName: nameof<RiskLinkableModel>((x) => x.organization),
                    fieldType: GenericListFieldType.TextArea,
                },
            ],

            dataMethod: () => {
                return this._ds.getLinkableRisks(this.auditGuid).pipe(
                    map(async (data) => {
                        const newLinks = await firstValueFrom(
                            this._ds.getFieldworkLinkedRisks(this.auditGuid, this.stepGuid)
                        );

                        this.linkedRisks.splice(0, this.linkedRisks.length);
                        this.linkedRisks.push(...newLinks);

                        let linked: RiskLinkableModel[] = [];
                        let unlinked: RiskLinkableModel[] = [];

                        if (this.linkedRisks.length === 0) {
                            unlinked = data;
                        } else {
                            data.forEach((row) => {
                                if (this.linkedRisks.indexOf(row.id) >= 0) {
                                    linked.push(row);
                                } else {
                                    unlinked.push(row);
                                }
                            });
                        }

                        this.tableConfig.linkData.unlinked = unlinked;
                        return linked;
                    })
                );
            },

            addOverride: () => {
                this.openRisk(null);
            },
            editOverride: (row) => {
                this.openRisk(row);
            },

            onLinkedDataChange: (data: RiskLinkableModel[]) => {
                this.linkedRisks.splice(0, this.linkedRisks.length);
                this.linkedRisks.push(...data.map((d) => d.id));
            },
        };
    }
}
